/**
 * Application Constants
 * Contains all the constant values used throughout the application
 * Organized by category with validation and type safety
 */

import { env } from '../utils/env';
import type { BagSize } from '../types/api';

// ============================================================================
// Application Information
// ============================================================================

export const APP_INFO = {
  NAME: 'Gestión de Consumos Energéticos',
  VERSION: '2.0.0',
  DESCRIPTION: 'React + TypeScript Migration',
  BUILD_DATE: new Date().toISOString(),
  ENVIRONMENT: env.nodeEnv,
} as const;

// ============================================================================
// API Configuration
// ============================================================================

export const API_CONFIG = {
  BASE_URL: env.apiBaseUrl,
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 1,
  RETRY_DELAY: 1000, // 1 second
} as const;

// ============================================================================
// Local Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  USER_PREFERENCES: 'user_preferences',
  DASHBOARD_SETTINGS: 'dashboard_settings',
  THEME: 'theme',
  /**
   * Auth/session tokens must never live in localStorage. Keep this list scoped to non-sensitive data.
   */
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

// ============================================================================
// Application Routes
// ============================================================================

export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  PROFILE_SETUP: '/auth/profile-setup',

  // Fully public: no session, rendered outside ProtectedRoute
  PUBLIC_QR_SHARE: '/share/q/:token',

  // Protected routes
  DASHBOARD: '/',
  UPLOAD: '/upload',
  MANUAL_ENTRY: '/manual',
  INVOICE_RESULTS: '/invoice-results',
  USER_FILES: '/user-files',
  SMART_SORTING: '/user-files?view=classification',
} as const;

// ============================================================================
// Service Types
// ============================================================================

export const SERVICE_TYPES = {
  ELECTRICITY: 'electricidad',
  WATER: 'agua',
  GAS: 'gas',
} as const;

export type ServiceType = (typeof SERVICE_TYPES)[keyof typeof SERVICE_TYPES];

// Service type configurations
export const SERVICE_CONFIGS = {
  [SERVICE_TYPES.ELECTRICITY]: {
    label: 'Electricidad',
    unit: 'kWh',
    color: '#fbbf24',
  },
  [SERVICE_TYPES.WATER]: {
    label: 'Agua',
    unit: 'L',
    color: '#3b82f6',
  },
  [SERVICE_TYPES.GAS]: {
    label: 'Gas',
    unit: 'm³',
    color: '#ef4444',
  },
} as const;

// ============================================================================
// Bag Size Configuration
// ============================================================================

export interface BagSizeOption {
  value: BagSize;
  label: string;
  description: string;
  icon?: string;
}

export const BAG_SIZE_OPTIONS: ReadonlyArray<BagSizeOption> = [
  {
    value: 'small',
    label: 'Bolsa pequeña',
    description: '≈ 5 kg',
    icon: 'bag',
  },
  {
    value: 'medium',
    label: 'Bolsa mediana',
    description: '≈ 10 kg',
    icon: 'bag',
  },
  {
    value: 'big',
    label: 'Bolsa grande',
    description: '≈ 15 kg',
    icon: 'bag',
  },
] as const;

export const BAG_CONVERSION_FACTORS: Record<BagSize, number> = {
  small: 5,
  medium: 10,
  big: 15,
};

// ============================================================================
// Currency & Localization
// ============================================================================

/**
 * @deprecated Use currency utilities from '@utils/currency' instead.
 * Currency is now determined automatically based on user's country.
 */
export const CURRENCY = {
  DEFAULT: 'EUR',
  SYMBOL: '€',
  LOCALE: 'es-ES',
} as const;

// ============================================================================
// Date & Time Formats
// ============================================================================

export const DATE_FORMATS = {
  DISPLAY: 'dd/MM/yyyy',
  API: 'yyyy-MM-dd',
  DATETIME: 'dd/MM/yyyy HH:mm',
  TIME: 'HH:mm',
  MONTH_YEAR: 'MMMM yyyy',
} as const;

export const TIME_ZONES = {
  DEFAULT: 'Europe/Madrid',
} as const;

// ============================================================================
// Validation Constants
// ============================================================================

export const VALIDATION_RULES = {
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 128,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBERS: true,
    REQUIRE_SYMBOLS: false,
  },
  FILE: {
    MAX_SIZE: 5 * 1024 * 1024, // 5MB
    ALLOWED_TYPES: ['image/jpeg', 'image/png', 'application/pdf'] as const,
    ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.pdf'] as const,
  },
  TEXT: {
    MIN_NAME_LENGTH: 2,
    MAX_NAME_LENGTH: 100,
    MIN_MESSAGE_LENGTH: 10,
    MAX_MESSAGE_LENGTH: 1000,
  },
  NUMERIC: {
    MIN_COVERS: 1,
    MAX_COVERS: 10000,
    MIN_CONSUMPTION: 1,
    MAX_CONSUMPTION: 1000000,
    MIN_POSITIVE_INTEGER: 1,
  },
  DATE: {
    MIN_YEAR: 1940,
    MAX_YEAR: 2200,
    MIN_DATE: '1940-01-01',
    MAX_DATE: '2200-01-01',
  },
} as const;

// ============================================================================
// UI Constants
// ============================================================================

export const UI_CONFIG = {
  TOAST_DURATION: 4000,
  LOADING_DEBOUNCE: 300,
  ANIMATION_DURATION: 300,
  MODAL_Z_INDEX: 1000,
  DROPDOWN_Z_INDEX: 900,
  TOOLTIP_Z_INDEX: 800,
} as const;

export const BREAKPOINTS = {
  MOBILE: 768,
  TABLET: 1024,
  DESKTOP: 1200,
  LARGE: 1440,
} as const;

// ============================================================================
// Feature Flags
// ============================================================================

export const FEATURE_FLAGS = {
  ENABLE_ANALYTICS: env.enableAnalytics,
  ENABLE_DEV_TOOLS: env.enableDevTools,
  ENABLE_FILE_UPLOAD: true,
  ENABLE_MANUAL_ENTRY: true,
  ENABLE_EXPORT: false, // Future feature
  ENABLE_NOTIFICATIONS: false, // Future feature
  MARKETING_REPORTS: env.featureMarketingReports,
  COMPLIANCE: env.featureCompliance,
} as const;

export const MARKETING_REPORT_ALLOWED_HOSTS = env.marketingReportAllowedHosts;

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Error de conexión. Verifica tu conexión a internet.',
  AUTH_ERROR: 'Error de autenticación. Por favor, inicia sesión nuevamente.',
  VALIDATION_ERROR: 'Por favor, corrige los errores en el formulario.',
  FILE_TOO_LARGE: `El archivo es demasiado grande. Máximo: ${Math.round(VALIDATION_RULES.FILE.MAX_SIZE / 1024 / 1024)}MB`,
  INVALID_FILE_TYPE: `Tipo de archivo no válido. Solo se permiten: ${VALIDATION_RULES.FILE.ALLOWED_EXTENSIONS.join(', ')}`,
  GENERIC_ERROR:
    'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.',
} as const;

// ============================================================================
// Success Messages
// ============================================================================

export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: '¡Bienvenido! Has iniciado sesión correctamente.',
  REGISTER_SUCCESS: '¡Cuenta creada! Por favor verifica tu email.',
  PROFILE_UPDATE_SUCCESS: 'Perfil actualizado correctamente.',
  FILE_UPLOAD_SUCCESS: 'Archivo procesado correctamente.',
  DATA_SAVE_SUCCESS: 'Datos guardados correctamente.',
} as const;

// ============================================================================
// Analytics Events
// ============================================================================

export const ANALYTICS_EVENTS = {
  PAGE_VIEW: 'page_view',
  LOGIN: 'login',
  LOGOUT: 'logout',
  FILE_UPLOAD: 'file_upload',
  FORM_SUBMIT: 'form_submit',
  ERROR: 'error',
} as const;

// ============================================================================
// Utility Functions for Constants
// ============================================================================

/**
 * Get service configuration by type
 */
export function getServiceConfig(serviceType: ServiceType) {
  return SERVICE_CONFIGS[serviceType];
}

/**
 * Check if file type is allowed
 */
export function isValidFileType(fileType: string): boolean {
  return new Set<string>(VALIDATION_RULES.FILE.ALLOWED_TYPES).has(fileType);
}

/**
 * Check if file extension is allowed
 */
export function isValidFileExtension(filename: string): boolean {
  const extension = `.${filename.split('.').pop()?.toLowerCase()}`;
  return new Set<string>(VALIDATION_RULES.FILE.ALLOWED_EXTENSIONS).has(
    extension,
  );
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Validate password strength
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const rules = VALIDATION_RULES.PASSWORD;

  if (password.length < rules.MIN_LENGTH) {
    errors.push(
      `La contraseña debe tener al menos ${rules.MIN_LENGTH} caracteres`,
    );
  }

  if (rules.REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) {
    errors.push('La contraseña debe contener al menos una letra mayúscula');
  }

  if (rules.REQUIRE_LOWERCASE && !/[a-z]/.test(password)) {
    errors.push('La contraseña debe contener al menos una letra minúscula');
  }

  if (rules.REQUIRE_NUMBERS && !/\d/.test(password)) {
    errors.push('La contraseña debe contener al menos un número');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// ============================================================================
// Legacy Exports (for backward compatibility)
// ============================================================================

// @deprecated Use APP_INFO instead
export const APP_NAME = APP_INFO.NAME;
export const APP_VERSION = APP_INFO.VERSION;
export const APP_DESCRIPTION = APP_INFO.DESCRIPTION;

// @deprecated Use API_CONFIG instead
export const API_BASE_URL = API_CONFIG.BASE_URL;
export const API_TIMEOUT = API_CONFIG.TIMEOUT;

// @deprecated Use STORAGE_KEYS instead
export const LOCAL_STORAGE_KEYS = STORAGE_KEYS;

// @deprecated Use VALIDATION_RULES instead
export const VALIDATION = VALIDATION_RULES;
